import React from 'react';
import { ShoppingCart, User, Heart, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template4 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Mobile-optimized Header */}
                        {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template4')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            URBAN STYLE
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template4/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template4/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template4')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template4/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template4/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template4/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>

            <main>
                {/* Hero Slider Section */}
                <section className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] px-3 lg:px-8 py-4 lg:py-8">
                    <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-xl">
                        <img
                            src="https://placehold.co/1920x1080/FFA500/FFFFFF?text=Hero+Slider"
                            alt="Hero Slider"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/30"></div>
                    </div>
                </section>

                {/* Profile Section */}
                <section className="py-4 lg:py-8 px-3 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
                            <div className="relative">
                                <img
                                    src="https://placehold.co/200x200/E0E0E0/333333?text=Profile"
                                    className="w-52 h-52 sm:w-72 sm:h-72 object-cover border-4 border-white shadow-2xl rounded-full"
                                    alt="Designer Profile"
                                />
                            </div>
                            <div className="flex-1 text-center lg:text-left space-y-2">
                                <h2 className="text-3xl lg:text-5xl font-extrabold text-gray-800">Designer Name</h2>
                                <p className="text-base lg:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0">
                                    A comprehensive promotional layout with stunning visuals
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Collection Banners - 3 Columns */}
                <section className="py-8 lg:py-16 px-3 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        <h2 className="text-center text-2xl lg:text-4xl font-bold text-gray-800 mb-4 lg:mb-8">Discover Our Collections</h2>
                        <p className="text-center text-sm lg:text-base text-gray-500 mb-6 lg:mb-10">Curated styles for every occasion</p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-8">
                            {[1, 2, 3].map((id) => (
                                <div key={id} className="group cursor-pointer text-center active:scale-95 lg:hover:scale-[1.02] transition-all duration-300">
                                    <div className="relative h-80 lg:h-96 w-full overflow-hidden rounded-lg shadow-md">
                                        <img
                                            src={`https://placehold.co/400x500/F0F0F0/333333?text=Banner+${id}`}
                                            alt={`Banner ${id}`}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="mt-3 lg:mt-4">
                                        <h3 className="text-base lg:text-lg font-bold text-gray-900">Collection {id}</h3>
                                        <p className="mt-1 text-xs lg:text-sm text-gray-600">Explore our special offers</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Trend Showcase - Curved Containers */}
                <section className="py-8 lg:py-16 px-3 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        <h2 className="text-center text-2xl lg:text-4xl font-bold text-gray-900 mb-6 lg:mb-12">Trending Styles</h2>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-8">
                            {[1, 2, 3, 4].map((id) => (
                                <div key={id} className="text-center group cursor-pointer">
                                    <div className="relative bg-gray-100 rounded-t-[200px] h-80 sm:h-96 md:h-[450px] overflow-hidden shadow-lg active:shadow-xl lg:hover:shadow-2xl transition-all duration-300">
                                        <img
                                            src={`https://placehold.co/300x500/E0E0E0/333333?text=Trend+${id}`}
                                            alt={`Trend ${id}`}
                                            className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[95%] w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                                        />
                                    </div>
                                    <h3 className="mt-4 text-sm lg:text-base font-semibold text-gray-900">Trend {id}</h3>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Video Carousel */}
                <section className="py-8 lg:py-16">
                    <div className="max-w-7xl mx-auto px-3 lg:px-8">
                        <h2 className="text-center text-2xl lg:text-4xl font-bold text-gray-800 mb-6 lg:mb-10">Latest Video Reels</h2>

                        <div className="flex overflow-x-auto space-x-4 lg:space-x-6 pb-4 -mx-3 px-3 lg:mx-0 lg:px-0 scrollbar-hide snap-x snap-mandatory">
                            {[1, 2, 3].map((id) => (
                                <div key={id} className="flex-none w-80 md:w-96 rounded-2xl overflow-hidden shadow-xl bg-white snap-start active:scale-95 lg:hover:shadow-2xl transition-all duration-300">
                                    <div className="aspect-video overflow-hidden bg-gray-900">
                                        <video
                                            controls
                                            poster="https://placehold.co/600x400/E0E0E0/333333?text=Video+Thumbnail"
                                            className="w-full h-full object-cover"
                                        >
                                            <source src="https://www.w3schools.com/html/mov_bbb.mp4" type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                    <div className="p-4 lg:p-6">
                                        <h3 className="text-base lg:text-xl font-semibold text-gray-900">Video Title {id}</h3>
                                        <p className="text-xs lg:text-sm text-gray-600 mt-2">Watch our latest collection showcase</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Product Grid */}
                <section className="w-full bg-white py-8 lg:py-16 px-3 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-6 lg:mb-12">
                            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-2 lg:mb-4">All Products</h2>
                            <p className="text-base lg:text-xl text-gray-600">Discover our latest collection</p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 lg:gap-6">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(id => (
                                <div key={id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 active:scale-95 lg:hover:shadow-2xl lg:hover:-translate-y-2 lg:hover:border-gray-900 transition-all duration-300 group">
                                    <div className="aspect-[4/5] overflow-hidden relative bg-gray-50">
                                        <img
                                            src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`}
                                            alt={`Product ${id}`}
                                            className="object-cover object-center w-full h-full group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-3 lg:p-6">
                                        <h3 className="text-sm lg:text-lg font-semibold text-gray-900 mb-2 line-clamp-2">Product Title {id}</h3>
                                        <p className="text-base lg:text-2xl font-bold text-gray-900 mb-3 lg:mb-4">${(69 + id * 10)}.00</p>
                                        <button className="w-full bg-gray-900 text-white py-2 lg:py-3 px-4 lg:px-6 rounded-lg text-xs lg:text-sm font-medium active:bg-gray-800 lg:hover:bg-gray-800 transition-all duration-300">
                                            Add to Cart
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </main>

            {/* Footer */}
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">URBAN STYLE</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template4')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template4/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template4/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template4/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@urbanstyle.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 URBAN STYLE. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template4;
